/**
 * ConsentManager — Phase 2c Granular Data Trust Center.
 *
 * Replaces the legacy cookie banner with a multi-toggle modal that
 * lets the visitor explicitly opt-in to each data-processing surface.
 *
 * Surfaces
 *   • essential        — cannot be turned off (auth, billing)
 *   • ai_processing    — Google Gemini API generates paths/doubts
 *   • analytics        — PostHog (anonymised product analytics)
 *   • personalisation  — local persona/learning-style storage
 *
 * All toggles persist to localStorage under "oak_consent_v1" + are
 * mirrored to the backend for users who later sign in (so consent
 * follows the account, not just the device).
 *
 * The banner only renders if no decision has been recorded. After a
 * decision, the user can re-open the panel from Settings → Trust
 * Center.
 */
import { useEffect, useState } from "react";
import { Shield, Sparkles, Activity, User, Globe2, X } from "lucide-react";
import useJurisdiction, { REGION_META } from "@/hooks/useJurisdiction";
import { api } from "@/lib/api";

const STORAGE_KEY = "oak_consent_v1";
const DEFAULTS = {
  essential: true,
  ai_processing: true,
  analytics: false,
  personalisation: true,
  decided_at: null,
  region: null,
};

function loadDecision() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return null;
  }
}

function saveDecision(d) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  } catch {
    /* ignore */
  }
  api.post("/consent/record", d).catch(() => {});
}

export default function ConsentManager({ open: forceOpen = false, onClose }) {
  const [decision, setDecision] = useState(() => loadDecision());
  const [draft, setDraft] = useState(decision || DEFAULTS);
  const [open, setOpen] = useState(forceOpen || !decision);
  const { region, setRegion } = useJurisdiction();

  // Sync draft.region with the live jurisdiction signal so the user
  // sees their current detected region even before they save.
  useEffect(() => {
    if (region && !draft.region) setDraft((d) => ({ ...d, region }));
  }, [region, draft.region]);

  // Re-open if the parent toggles forceOpen.
  useEffect(() => {
    setOpen(forceOpen || !decision);
  }, [forceOpen, decision]);

  if (!open) return null;

  const accept = () => {
    const next = {
      ...DEFAULTS,
      ...draft,
      decided_at: new Date().toISOString(),
      region: draft.region || region,
    };
    saveDecision(next);
    if (next.region) setRegion(next.region);
    setDecision(next);
    setOpen(false);
    onClose?.();
  };

  const acceptAll = () => {
    setDraft({
      ...DEFAULTS,
      ai_processing: true,
      analytics: true,
      personalisation: true,
      region: draft.region || region,
    });
    setTimeout(accept, 0);
  };

  const rejectOptional = () => {
    setDraft({
      ...DEFAULTS,
      ai_processing: false,
      analytics: false,
      personalisation: false,
      region: draft.region || region,
    });
    setTimeout(accept, 0);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] p-4 sm:p-6 pointer-events-none"
      data-testid="consent-manager-root"
    >
      <div
        role="dialog"
        aria-label="Data Trust Center"
        className="pointer-events-auto max-w-3xl mx-auto rounded-2xl border border-white/12 bg-[#0B0E14]/95 backdrop-blur-xl shadow-2xl p-5 sm:p-6 space-y-5"
        data-testid="consent-modal"
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <div className="label-eyebrow !text-[10px] tracking-[0.25em] text-[#00FF9D]/85 flex items-center gap-1.5">
              <Shield size={11} /> Data Trust Center
            </div>
            <h2 className="font-display text-lg sm:text-xl tracking-tight mt-1">
              How would you like Oak AI Campus to use your data?
            </h2>
            <p className="text-white/55 text-[13px] leading-relaxed mt-1">
              We process the minimum needed to teach you. Toggle each surface — your choice is
              binding and you can change it later from Settings → Trust Center.
            </p>
          </div>
          {decision && (
            <button
              onClick={() => {
                setOpen(false);
                onClose?.();
              }}
              className="text-white/40 hover:text-white"
              data-testid="consent-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          )}
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ConsentRow
            icon={<Shield size={13} />}
            title="Essential"
            body="Account, billing, security."
            value
            disabled
            testid="consent-essential"
          />
          <ConsentRow
            icon={<Sparkles size={13} />}
            title="AI Processing — Google Gemini"
            body="Send your goals + study questions to the Gemini API to generate paths and answer doubts. Without this, paths cannot be generated."
            value={draft.ai_processing}
            onChange={(v) => setDraft({ ...draft, ai_processing: v })}
            testid="consent-ai-processing"
          />
          <ConsentRow
            icon={<Activity size={13} />}
            title="Product analytics — PostHog"
            body="Anonymised usage events so we know which features to invest in. Off by default."
            value={draft.analytics}
            onChange={(v) => setDraft({ ...draft, analytics: v })}
            testid="consent-analytics"
          />
          <ConsentRow
            icon={<User size={13} />}
            title="Personalisation"
            body="Save your learning-style preferences and language locally so paths feel native."
            value={draft.personalisation}
            onChange={(v) => setDraft({ ...draft, personalisation: v })}
            testid="consent-personalisation"
          />
        </div>

        {/* Data residency selector */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="label-eyebrow !text-[10px] tracking-[0.25em] flex items-center gap-1.5 text-white/60">
            <Globe2 size={11} /> Data residency
          </div>
          <div className="text-[12.5px] text-white/70 mt-1">
            Detected: <span className="text-white">{REGION_META[region]?.label || "Global"}</span>{" "}
            · stored in <span className="text-white/85">{REGION_META[region]?.residency || "ap-south-1"}</span>{" "}
            · governed by <span className="text-white/85">{REGION_META[region]?.regulation || "best-effort"}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="consent-region-options">
            {Object.entries(REGION_META).map(([code, meta]) => (
              <button
                key={code}
                onClick={() => setDraft({ ...draft, region: code })}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  (draft.region || region) === code
                    ? "border-[#00FF9D]/50 bg-[#00FF9D]/[0.10] text-[#9bffcc]"
                    : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white/85"
                }`}
                data-testid={`consent-region-${code}`}
              >
                {meta.label}
              </button>
            ))}
          </div>
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <button
            onClick={rejectOptional}
            className="text-[12.5px] px-3.5 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition"
            data-testid="consent-reject"
          >
            Reject optional
          </button>
          <button
            onClick={accept}
            className="text-[12.5px] px-3.5 py-2 rounded-full border border-white/15 bg-white/[0.04] text-white/85 hover:bg-white/[0.10] transition"
            data-testid="consent-save"
          >
            Save my choices
          </button>
          <button
            onClick={acceptAll}
            className="text-[12.5px] px-3.5 py-2 rounded-full bg-gradient-to-r from-[#0044FF] to-[#5D3FD3] text-white font-display font-semibold shadow-[0_4px_18px_-4px_rgba(0,68,255,0.6)] hover:opacity-95 transition"
            data-testid="consent-accept-all"
          >
            Accept all
          </button>
        </footer>
      </div>
    </div>
  );
}

function ConsentRow({ icon, title, body, value, onChange, disabled, testid }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        value ? "border-[#00FF9D]/22 bg-[#00FF9D]/[0.04]" : "border-white/10 bg-white/[0.02]"
      }`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[12.5px] text-white/85 font-display font-semibold flex items-center gap-1.5">
            {icon} {title}
          </div>
          <div className="text-[11.5px] text-white/55 mt-0.5 leading-relaxed">{body}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          disabled={disabled}
          onClick={() => onChange?.(!value)}
          className={`shrink-0 w-9 h-5 rounded-full border transition relative ${
            value
              ? "border-[#00FF9D]/50 bg-[#00FF9D]/[0.22]"
              : "border-white/20 bg-white/[0.04]"
          } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          data-testid={`${testid}-toggle`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
              value ? "left-[16px] bg-[#00FF9D]" : "left-0.5 bg-white/60"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

/** Read the current consent decision from anywhere. Returns null
 * before the user has made a choice. */
export function readConsent() {
  return loadDecision();
}
